(() => {
  const registryName = 'PormidoProductFeatureViewerRegistry';
  const registryVersion = '2.3.0';
  const rootSelector = '[data-pormido-product-feature-viewer]';
  const desktopQuery = '(min-width: 750px)';
  const wheelThreshold = 72;
  const wheelGestureSilence = 220;
  const swipeThreshold = 40;
  const swipeAxisRatio = 1.2;

  const existingRegistry = window[registryName];

  if (existingRegistry?.version === registryVersion) {
    existingRegistry.bindGlobal();
    existingRegistry.initWithin(document);
    return;
  }

  existingRegistry?.teardown?.();

  class PormidoProductFeatureViewer {
    constructor(root) {
      this.root = root;
      this.navigationItems = Array.from(root.querySelectorAll('[data-ppfv-navigation-item]'));
      this.navigationButtons = Array.from(root.querySelectorAll('[data-ppfv-navigation-button]'));
      this.descriptions = Array.from(root.querySelectorAll('[data-ppfv-description]'));
      this.panels = Array.from(root.querySelectorAll('[data-ppfv-panel]'));
      this.mediaStage = root.querySelector('[data-ppfv-media-stage]');
      this.navigation = root.querySelector('.pormido-product-feature-viewer__navigation');
      this.previousButton = root.querySelector('[data-ppfv-previous]');
      this.nextButton = root.querySelector('[data-ppfv-next]');
      this.counter = root.querySelector('[data-ppfv-counter]');
      this.indicatorButtons = Array.from(root.querySelectorAll('[data-ppfv-indicator]'));
      this.mobileTextRegions = this.panels.map((panel) =>
        panel.querySelector('[data-ppfv-mobile-text]')
      );
      this.textToggleButtons = this.mobileTextRegions
        .map((region) => region?.querySelector('[data-ppfv-text-toggle]'))
        .filter(Boolean);
      this.videos = Array.from(root.querySelectorAll('[data-ppfv-video]'));
      this.desktopMedia = window.matchMedia(desktopQuery);
      this.mode = this.desktopMedia.matches ? 'desktop' : 'mobile';
      this.activeIndex = Math.max(
        0,
        this.panels.findIndex((panel) => panel.classList.contains('is-active'))
      );
      this.isSectionVisible = this.isRootVisible();
      this.visibilityObserver = null;
      this.resizeObserver = null;
      this.isTransitioning = false;
      this.transitionPhaseTimer = null;
      this.transitionCleanupTimer = null;
      this.transitionFrame = null;
      this.transitionEndTarget = null;
      this.wheelBound = false;
      this.wheelAccumulator = 0;
      this.wheelDirection = 0;
      this.wheelGestureConsumed = false;
      this.wheelGestureTimer = null;
      this.swipeBound = false;
      this.swipeStart = null;
      this.swipeLast = null;
      this.swipePointerId = null;
      this.swipeAxis = null;
      this.textMeasureFrame = null;
      this.isDestroyed = false;

      this.handleModeChange = this.handleModeChange.bind(this);
      this.handleNavigationClick = this.handleNavigationClick.bind(this);
      this.handleNavigationKeydown = this.handleNavigationKeydown.bind(this);
      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleIndicatorClick = this.handleIndicatorClick.bind(this);
      this.handleTextToggle = this.handleTextToggle.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerEnd = this.handlePointerEnd.bind(this);
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
      this.handleWheel = this.handleWheel.bind(this);
      this.handleDocumentVisibility = this.handleDocumentVisibility.bind(this);

      this.navigationButtons.forEach((button) => {
        button.addEventListener('click', this.handleNavigationClick);
        button.addEventListener('keydown', this.handleNavigationKeydown);
      });
      this.previousButton?.addEventListener('click', this.handlePrevious);
      this.nextButton?.addEventListener('click', this.handleNext);
      this.indicatorButtons.forEach((button) => {
        button.addEventListener('click', this.handleIndicatorClick);
      });
      this.textToggleButtons.forEach((button) => {
        button.addEventListener('click', this.handleTextToggle);
      });
      this.addMediaListener(this.desktopMedia, this.handleModeChange);
      document.addEventListener('visibilitychange', this.handleDocumentVisibility);

      this.initVisibilityObserver();
      this.initResizeObserver();
      this.root.classList.add('is-enhanced');
      this.root.dataset.ppfvMode = this.mode;
      this.syncWheelBinding();
      this.syncSwipeBinding();
      this.render(false);
      this.prepareAdjacentImages();
    }

    addMediaListener(mediaQuery, listener) {
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', listener);
      } else {
        mediaQuery.addListener(listener);
      }
    }

    removeMediaListener(mediaQuery, listener) {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', listener);
      } else {
        mediaQuery.removeListener(listener);
      }
    }

    isRootVisible() {
      const rect = this.root.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    }

    initVisibilityObserver() {
      if (!('IntersectionObserver' in window)) return;

      this.visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          this.isSectionVisible = Boolean(entry?.isIntersecting);
          this.syncVideoPlayback();
        },
        { threshold: 0.05 }
      );

      this.visibilityObserver.observe(this.root);
    }

    initResizeObserver() {
      if (!('ResizeObserver' in window)) return;

      this.resizeObserver = new ResizeObserver(() => {
        if (this.mode === 'mobile') {
          this.scheduleMobileTextMeasurement();
        }
      });

      this.resizeObserver.observe(this.root);
    }

    syncWheelBinding() {
      const shouldBind = this.mode === 'desktop' && Boolean(this.mediaStage);

      if (shouldBind && !this.wheelBound) {
        this.mediaStage.addEventListener('wheel', this.handleWheel, { passive: false });
        this.wheelBound = true;
      } else if (!shouldBind && this.wheelBound) {
        this.mediaStage.removeEventListener('wheel', this.handleWheel);
        this.wheelBound = false;
      }
    }

    syncSwipeBinding() {
      const shouldBind = this.mode === 'mobile' && Boolean(this.mediaStage);

      if (shouldBind && !this.swipeBound) {
        this.mediaStage.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
        this.mediaStage.addEventListener('pointermove', this.handlePointerMove, { passive: false });
        this.mediaStage.addEventListener('pointerup', this.handlePointerEnd, { passive: true });
        this.mediaStage.addEventListener('pointercancel', this.handlePointerEnd, { passive: true });
        this.swipeBound = true;
      } else if (!shouldBind && this.swipeBound) {
        this.removeSwipeListeners();
      }
    }

    removeSwipeListeners() {
      if (!this.mediaStage || !this.swipeBound) return;

      this.mediaStage.removeEventListener('pointerdown', this.handlePointerDown);
      this.mediaStage.removeEventListener('pointermove', this.handlePointerMove);
      this.mediaStage.removeEventListener('pointerup', this.handlePointerEnd);
      this.mediaStage.removeEventListener('pointercancel', this.handlePointerEnd);
      this.swipeBound = false;
      this.resetSwipeGesture();
    }

    resetSwipeGesture() {
      this.swipeStart = null;
      this.swipeLast = null;
      this.swipePointerId = null;
      this.swipeAxis = null;
    }

    handlePointerDown(event) {
      if (
        this.mode !== 'mobile' ||
        !event.isPrimary ||
        (event.pointerType === 'mouse' && event.button !== 0)
      ) {
        this.resetSwipeGesture();
        return;
      }

      const target = event.target instanceof Element ? event.target : null;
      if (
        !target?.closest('.pormido-product-feature-viewer__mobile-media-frame') ||
        target.closest('a, button, input, select, textarea')
      ) {
        this.resetSwipeGesture();
        return;
      }

      this.swipePointerId = event.pointerId;
      this.swipeAxis = 'pending';
      this.swipeStart = { x: event.clientX, y: event.clientY };
      this.swipeLast = { x: event.clientX, y: event.clientY };
    }

    handlePointerMove(event) {
      if (!this.swipeStart || event.pointerId !== this.swipePointerId) return;

      this.swipeLast = { x: event.clientX, y: event.clientY };
      const deltaX = event.clientX - this.swipeStart.x;
      const deltaY = event.clientY - this.swipeStart.y;
      const horizontalDistance = Math.abs(deltaX);
      const verticalDistance = Math.abs(deltaY);

      if (this.swipeAxis === 'pending') {
        if (horizontalDistance < 8 && verticalDistance < 8) return;

        if (horizontalDistance > verticalDistance * swipeAxisRatio) {
          this.swipeAxis = 'horizontal';
          this.mediaStage?.setPointerCapture?.(event.pointerId);
        } else if (verticalDistance > horizontalDistance) {
          this.swipeAxis = 'vertical';
        } else {
          return;
        }
      }

      if (this.swipeAxis === 'horizontal' && event.cancelable) {
        event.preventDefault();
      }
    }

    handlePointerEnd(event) {
      if (!this.swipeStart || event.pointerId !== this.swipePointerId) return;

      const end = this.swipeLast || { x: event.clientX, y: event.clientY };
      const deltaX = end.x - this.swipeStart.x;
      const deltaY = end.y - this.swipeStart.y;
      const horizontalDistance = Math.abs(deltaX);
      const verticalDistance = Math.abs(deltaY);
      const isHorizontal =
        this.swipeAxis === 'horizontal' ||
        (this.swipeAxis === 'pending' && horizontalDistance > verticalDistance * swipeAxisRatio);
      const shouldActivate =
        event.type === 'pointerup' && isHorizontal && horizontalDistance >= swipeThreshold;

      if (this.mediaStage?.hasPointerCapture?.(event.pointerId)) {
        this.mediaStage.releasePointerCapture(event.pointerId);
      }

      this.resetSwipeGesture();

      if (shouldActivate) {
        this.activate(this.activeIndex + (deltaX < 0 ? 1 : -1));
      }
    }

    normalizeWheelDelta(event) {
      let delta = event.deltaY;

      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
        delta *= 16;
      } else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
        delta *= Math.max(this.mediaStage?.clientHeight || window.innerHeight, 1);
      }

      return delta;
    }

    resetWheelGesture(resetConsumed = true) {
      this.wheelAccumulator = 0;
      this.wheelDirection = 0;

      if (resetConsumed) {
        this.wheelGestureConsumed = false;
      }

      if (this.wheelGestureTimer) {
        window.clearTimeout(this.wheelGestureTimer);
        this.wheelGestureTimer = null;
      }
    }

    queueWheelGestureReset() {
      if (this.wheelGestureTimer) {
        window.clearTimeout(this.wheelGestureTimer);
      }

      this.wheelGestureTimer = window.setTimeout(() => {
        this.resetWheelGesture();
      }, wheelGestureSilence);
    }

    navigationCanScroll(direction, eventTarget) {
      if (!this.navigation || !this.navigation.contains(eventTarget)) return false;

      const maxScrollTop = this.navigation.scrollHeight - this.navigation.clientHeight;
      if (maxScrollTop <= 1) return false;

      if (direction > 0) {
        return this.navigation.scrollTop < maxScrollTop - 1;
      }

      return this.navigation.scrollTop > 1;
    }

    handleWheel(event) {
      if (
        this.mode !== 'desktop' ||
        this.panels.length < 2 ||
        event.ctrlKey ||
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ) {
        this.resetWheelGesture();
        return;
      }

      const delta = this.normalizeWheelDelta(event);
      if (!Number.isFinite(delta) || Math.abs(delta) < 0.5) return;

      const direction = delta > 0 ? 1 : -1;
      const nextIndex = this.activeIndex + direction;
      const canMove = nextIndex >= 0 && nextIndex < this.panels.length;

      if (!canMove) {
        this.resetWheelGesture();
        return;
      }

      if (this.navigationCanScroll(direction, event.target)) {
        this.resetWheelGesture();
        return;
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      this.queueWheelGestureReset();

      if (this.wheelDirection !== 0 && this.wheelDirection !== direction) {
        this.wheelAccumulator = 0;
      }
      this.wheelDirection = direction;

      if (this.wheelGestureConsumed || this.isTransitioning) return;

      this.wheelAccumulator += Math.abs(delta);
      if (this.wheelAccumulator < wheelThreshold) return;

      this.wheelAccumulator = 0;
      this.wheelGestureConsumed = true;
      this.activate(nextIndex);
    }

    handleModeChange(event) {
      this.cancelTransition();
      this.resetWheelGesture();
      this.mode = event.matches ? 'desktop' : 'mobile';
      this.root.dataset.ppfvMode = this.mode;
      this.syncWheelBinding();
      this.syncSwipeBinding();
      this.render(false);
      this.prepareAdjacentImages();
    }

    handleNavigationClick(event) {
      const index = Number.parseInt(event.currentTarget.dataset.index, 10);
      this.activate(index);
    }

    handleNavigationKeydown(event) {
      const currentIndex = this.navigationButtons.indexOf(event.currentTarget);
      if (currentIndex < 0 || this.navigationButtons.length < 2) return;

      let focusIndex = null;

      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        focusIndex = (currentIndex + 1) % this.navigationButtons.length;
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        focusIndex = (currentIndex - 1 + this.navigationButtons.length) % this.navigationButtons.length;
      } else if (event.key === 'Home') {
        focusIndex = 0;
      } else if (event.key === 'End') {
        focusIndex = this.navigationButtons.length - 1;
      }

      if (focusIndex === null) return;

      event.preventDefault();
      this.navigationButtons[focusIndex]?.focus();
    }

    handlePrevious() {
      this.activate(this.activeIndex - 1);
    }

    handleNext() {
      this.activate(this.activeIndex + 1);
    }

    handleIndicatorClick(event) {
      const index = Number.parseInt(event.currentTarget.dataset.index, 10);
      this.activate(index);
    }

    handleTextToggle(event) {
      const region = event.currentTarget.closest('[data-ppfv-mobile-text]');
      if (!region) return;

      if (this.getMobileTextCollapseMode() === 'always_open') {
        event.currentTarget.hidden = true;
        return;
      }

      const willExpand = !region.classList.contains('is-expanded');
      region.classList.toggle('is-expanded', willExpand);
      region.classList.toggle('is-collapsed', !willExpand);
      event.currentTarget.setAttribute('aria-expanded', willExpand ? 'true' : 'false');
      event.currentTarget.textContent = willExpand ? '閉じる' : '続きを見る';
    }

    handleDocumentVisibility() {
      this.syncVideoPlayback();
    }

    getTransitionDuration() {
      const value = window
        .getComputedStyle(this.root)
        .getPropertyValue('--ppfv-transition-speed')
        .trim();
      const duration = Number.parseFloat(value);
      return Number.isFinite(duration) ? Math.max(duration, 0) : 350;
    }

    getMobileTransitionDuration() {
      const value = window
        .getComputedStyle(this.root)
        .getPropertyValue('--ppfv-mobile-transition-speed')
        .trim();
      const duration = Number.parseFloat(value);
      return Number.isFinite(duration) ? Math.max(duration, 0) : 260;
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    clearTransitionTimers() {
      if (this.transitionPhaseTimer) {
        window.clearTimeout(this.transitionPhaseTimer);
        this.transitionPhaseTimer = null;
      }

      if (this.transitionCleanupTimer) {
        window.clearTimeout(this.transitionCleanupTimer);
        this.transitionCleanupTimer = null;
      }

      if (this.transitionFrame) {
        window.cancelAnimationFrame(this.transitionFrame);
        this.transitionFrame = null;
      }

      if (this.textMeasureFrame) {
        window.cancelAnimationFrame(this.textMeasureFrame);
        this.textMeasureFrame = null;
      }

      this.detachTransitionEnd();
    }

    detachTransitionEnd() {
      this.transitionEndTarget?.removeEventListener('transitionend', this.handleTransitionEnd);
      this.transitionEndTarget = null;
    }

    handleTransitionEnd(event) {
      if (event.target !== this.transitionEndTarget || event.propertyName !== 'opacity') return;
      this.completeTransition();
    }

    completeTransition() {
      if (this.transitionPhaseTimer) {
        window.clearTimeout(this.transitionPhaseTimer);
        this.transitionPhaseTimer = null;
      }
      if (this.transitionCleanupTimer) {
        window.clearTimeout(this.transitionCleanupTimer);
        this.transitionCleanupTimer = null;
      }
      this.detachTransitionEnd();
      this.clearTransitionClasses();
      this.isTransitioning = false;
    }

    armMobileTransitionEnd(panel, duration) {
      this.detachTransitionEnd();
      this.transitionEndTarget = panel?.querySelector(
        '[data-ppfv-media-mode="mobile"]'
      );
      this.transitionEndTarget?.addEventListener('transitionend', this.handleTransitionEnd);
      this.transitionCleanupTimer = window.setTimeout(
        () => this.completeTransition(),
        duration + 100
      );
    }

    getMobileTextCollapseMode() {
      const mode = this.root.dataset.mobileTextCollapseMode;
      return mode === 'always_open' || mode === 'expanded' ? mode : 'collapsed';
    }

    resetMobileTextExpansion() {
      const mode = this.getMobileTextCollapseMode();

      this.mobileTextRegions.forEach((region) => {
        if (!region) return;

        const toggle = region.querySelector('[data-ppfv-text-toggle]');
        const startsExpanded = mode === 'always_open' || mode === 'expanded';

        region.classList.toggle('is-collapsed', !startsExpanded);
        region.classList.toggle('is-expanded', startsExpanded);
        region.classList.remove('has-overflow');

        if (toggle) {
          toggle.hidden = true;
          toggle.setAttribute('aria-expanded', startsExpanded ? 'true' : 'false');
          toggle.textContent = startsExpanded ? '閉じる' : '続きを見る';
        }
      });
    }

    scheduleMobileTextMeasurement() {
      if (this.textMeasureFrame) {
        window.cancelAnimationFrame(this.textMeasureFrame);
      }

      this.textMeasureFrame = window.requestAnimationFrame(() => {
        this.textMeasureFrame = null;
        if (this.isDestroyed || this.mode !== 'mobile') return;

        const region = this.mobileTextRegions[this.activeIndex];
        const content = region?.querySelector('[data-ppfv-mobile-description]');
        const toggle = region?.querySelector('[data-ppfv-text-toggle]');
        if (!region || !content || !toggle) return;

        const mode = this.getMobileTextCollapseMode();
        if (mode === 'always_open') {
          region.classList.remove('is-collapsed', 'has-overflow');
          region.classList.add('is-expanded');
          toggle.hidden = true;
          toggle.setAttribute('aria-expanded', 'true');
          toggle.textContent = '閉じる';
          return;
        }

        const wasExpanded = region.classList.contains('is-expanded');
        region.classList.remove('is-expanded');
        region.classList.add('is-collapsed');
        const hasOverflow = content.scrollHeight > content.clientHeight + 1;

        region.classList.toggle('has-overflow', hasOverflow);
        region.classList.toggle('is-collapsed', !wasExpanded);
        region.classList.toggle('is-expanded', wasExpanded);
        toggle.hidden = !hasOverflow;
        toggle.setAttribute('aria-expanded', wasExpanded ? 'true' : 'false');
        toggle.textContent = wasExpanded ? '閉じる' : '続きを見る';
      });
    }

    clearTransitionClasses() {
      this.panels.forEach((panel) => panel.classList.remove('is-outgoing', 'is-incoming'));
      this.root.classList.remove('is-transitioning');
    }

    cancelTransition() {
      this.clearTransitionTimers();
      this.clearTransitionClasses();
      this.isTransitioning = false;
    }

    pausePanelVideos(panel) {
      panel?.querySelectorAll('[data-ppfv-video]').forEach((video) => video.pause());
    }

    activate(index, announce = true) {
      if (this.panels.length === 0) return;

      const nextIndex = Math.min(Math.max(index, 0), this.panels.length - 1);
      if (nextIndex === this.activeIndex || this.isTransitioning) return;

      const duration = this.mode === 'mobile'
        ? this.getMobileTransitionDuration()
        : this.getTransitionDuration();
      if (duration < 20 || this.prefersReducedMotion()) {
        this.activeIndex = nextIndex;
        this.render(announce);
        return;
      }

      const outgoingPanel = this.panels[this.activeIndex];
      const phaseDuration = Math.min(100, Math.max(40, duration * 0.2));

      this.isTransitioning = true;
      this.root.classList.add('is-transitioning');
      outgoingPanel?.classList.add('is-outgoing');

      if (this.mode === 'mobile') {
        const incomingPanel = this.panels[nextIndex];
        incomingPanel?.classList.add('is-incoming');
        this.transitionFrame = window.requestAnimationFrame(() => {
          this.transitionFrame = null;
          if (this.isDestroyed) return;

          this.pausePanelVideos(outgoingPanel);
          this.activeIndex = nextIndex;
          this.render(announce, false);
          this.syncVideoPlayback();
          this.prepareAdjacentImages();
          this.armMobileTransitionEnd(incomingPanel, duration);
        });
        return;
      }

      this.transitionPhaseTimer = window.setTimeout(() => {
        this.transitionPhaseTimer = null;
        if (this.isDestroyed) return;

        this.pausePanelVideos(outgoingPanel);
        this.activeIndex = nextIndex;
        this.render(announce, false);
        this.syncVideoPlayback();

        this.transitionCleanupTimer = window.setTimeout(() => {
          this.transitionCleanupTimer = null;
          this.clearTransitionClasses();
          this.isTransitioning = false;
        }, duration);
      }, phaseDuration);
    }

    render(announce, syncVideos = true) {
      const total = this.panels.length;

      this.navigationItems.forEach((item, index) => {
        item.classList.toggle('is-active', index === this.activeIndex);
      });

      this.navigationButtons.forEach((button, index) => {
        button.setAttribute('aria-expanded', index === this.activeIndex ? 'true' : 'false');
      });

      this.descriptions.forEach((description, index) => {
        description.setAttribute('aria-hidden', index === this.activeIndex ? 'false' : 'true');
      });

      this.indicatorButtons.forEach((button, index) => {
        const isActive = index === this.activeIndex;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      this.panels.forEach((panel, index) => {
        const isActive = index === this.activeIndex;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        if (isActive) {
          panel.removeAttribute('inert');
        } else {
          panel.setAttribute('inert', '');
        }
      });

      if (this.previousButton) {
        this.previousButton.disabled = this.activeIndex === 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = total < 2 || this.activeIndex === total - 1;
      }

      if (this.counter) {
        const current = String(this.activeIndex + 1).padStart(2, '0');
        const count = String(total).padStart(2, '0');
        const nextText = `${current} / ${count}`;

        if (!announce) {
          const liveValue = this.counter.getAttribute('aria-live');
          this.counter.setAttribute('aria-live', 'off');
          this.counter.textContent = nextText;
          window.requestAnimationFrame(() => {
            if (!this.isDestroyed) {
              this.counter.setAttribute('aria-live', liveValue || 'polite');
            }
          });
        } else {
          this.counter.textContent = nextText;
        }
      }

      this.root.dataset.activeIndex = String(this.activeIndex);
      this.resetMobileTextExpansion();
      this.scheduleMobileTextMeasurement();
      if (syncVideos) {
        this.syncVideoPlayback();
      }
    }

    pauseAllVideos() {
      this.videos.forEach((video) => {
        video.pause();
      });
    }

    playVideo(video) {
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;

      if (!video.paused) return;

      const playPromise = video.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {});
      }
    }

    syncVideoPlayback() {
      this.pauseAllVideos();

      if (
        this.isDestroyed ||
        document.hidden ||
        !this.isSectionVisible ||
        this.panels.length === 0
      ) {
        return;
      }

      const activePanel = this.panels[this.activeIndex];
      const activeVideo = activePanel?.querySelector(
        `[data-ppfv-media-mode="${this.mode}"] [data-ppfv-video]`
      );

      if (activeVideo) {
        this.playVideo(activeVideo);
      }
    }

    prepareAdjacentImages() {
      if (this.mode !== 'mobile' || this.panels.length < 2) return;

      [this.activeIndex - 1, this.activeIndex + 1].forEach((index) => {
        if (index < 0 || index >= this.panels.length) return;
        const image = this.panels[index]?.querySelector(
          '[data-ppfv-media-mode="mobile"] img'
        );
        if (
          !image ||
          typeof image.decode !== 'function' ||
          image.dataset.ppfvDecoded === 'true' ||
          image.dataset.ppfvDecoding === 'true'
        ) {
          return;
        }

        image.dataset.ppfvDecoding = 'true';
        image
          .decode()
          .then(() => {
            image.dataset.ppfvDecoded = 'true';
          })
          .catch(() => {})
          .finally(() => {
            delete image.dataset.ppfvDecoding;
          });
      });
    }

    selectBlock(blockId) {
      const index = this.panels.findIndex((panel) => panel.dataset.blockId === blockId);
      if (index < 0) return false;

      this.activate(index, false);
      return true;
    }

    resetToNoJsState() {
      this.activeIndex = 0;

      this.navigationItems.forEach((item, index) => {
        item.classList.toggle('is-active', index === 0);
      });

      this.navigationButtons.forEach((button, index) => {
        button.setAttribute('aria-expanded', index === 0 ? 'true' : 'false');
      });

      this.descriptions.forEach((description, index) => {
        description.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      });

      this.indicatorButtons.forEach((button, index) => {
        button.classList.toggle('is-active', index === 0);
        button.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      });

      this.mobileTextRegions.forEach((region) => {
        if (!region) return;

        const toggle = region.querySelector('[data-ppfv-text-toggle]');
        region.classList.remove('is-collapsed', 'is-expanded');
        if (toggle) {
          toggle.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
          toggle.textContent = '続きを見る';
        }
      });

      this.panels.forEach((panel, index) => {
        panel.classList.remove('is-outgoing');
        panel.classList.toggle('is-active', index === 0);
        panel.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');

        if (index === 0) {
          panel.removeAttribute('inert');
        } else {
          panel.setAttribute('inert', '');
        }
      });

      this.root.classList.remove('is-enhanced');
      this.root.classList.remove('is-transitioning');
      delete this.root.dataset.ppfvMode;
      delete this.root.dataset.activeIndex;
    }

    destroy() {
      if (this.isDestroyed) return;
      this.isDestroyed = true;

      this.cancelTransition();
      this.resetWheelGesture();
      if (this.wheelBound) {
        this.mediaStage?.removeEventListener('wheel', this.handleWheel);
        this.wheelBound = false;
      }
      this.removeSwipeListeners();
      this.visibilityObserver?.disconnect();
      this.visibilityObserver = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.pauseAllVideos();
      this.removeMediaListener(this.desktopMedia, this.handleModeChange);
      document.removeEventListener('visibilitychange', this.handleDocumentVisibility);

      this.navigationButtons.forEach((button) => {
        button.removeEventListener('click', this.handleNavigationClick);
        button.removeEventListener('keydown', this.handleNavigationKeydown);
      });
      this.previousButton?.removeEventListener('click', this.handlePrevious);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.indicatorButtons.forEach((button) => {
        button.removeEventListener('click', this.handleIndicatorClick);
      });
      this.textToggleButtons.forEach((button) => {
        button.removeEventListener('click', this.handleTextToggle);
      });

      this.resetToNoJsState();
    }
  }

  const instances = new Map();
  let globalEventsBound = false;
  let domReadyHandler = null;

  const collectRoots = (scope) => {
    if (!scope) return [];

    const roots = [];
    if (scope.matches?.(rootSelector)) roots.push(scope);
    scope.querySelectorAll?.(rootSelector).forEach((root) => roots.push(root));
    return roots;
  };

  const registry = {
    version: registryVersion,

    init(root) {
      if (!root || instances.has(root)) return;
      instances.set(root, new PormidoProductFeatureViewer(root));
    },

    initWithin(scope) {
      collectRoots(scope).forEach((root) => this.init(root));
    },

    destroyWithin(scope) {
      collectRoots(scope).forEach((root) => {
        instances.get(root)?.destroy();
        instances.delete(root);
      });

      if (instances.size === 0) {
        this.unbindGlobal();
      }
    },

    selectBlock(blockId) {
      for (const instance of instances.values()) {
        if (instance.selectBlock(blockId)) return true;
      }
      return false;
    },

    bindGlobal() {
      if (globalEventsBound) return;

      document.addEventListener('shopify:section:load', handleSectionLoad);
      document.addEventListener('shopify:section:unload', handleSectionUnload);
      document.addEventListener('shopify:block:select', handleBlockSelect);
      globalEventsBound = true;
    },

    unbindGlobal() {
      if (!globalEventsBound) return;

      document.removeEventListener('shopify:section:load', handleSectionLoad);
      document.removeEventListener('shopify:section:unload', handleSectionUnload);
      document.removeEventListener('shopify:block:select', handleBlockSelect);
      globalEventsBound = false;
    },

    teardown() {
      instances.forEach((instance) => instance.destroy());
      instances.clear();
      this.unbindGlobal();

      if (domReadyHandler) {
        document.removeEventListener('DOMContentLoaded', domReadyHandler);
        domReadyHandler = null;
      }
    }
  };

  function handleSectionLoad(event) {
    registry.initWithin(event.target);
  }

  function handleSectionUnload(event) {
    registry.destroyWithin(event.target);
  }

  function handleBlockSelect(event) {
    const blockId = event.detail?.blockId;
    if (blockId) registry.selectBlock(blockId);
  }

  window[registryName] = registry;
  registry.bindGlobal();

  if (document.readyState === 'loading') {
    domReadyHandler = () => {
      registry.initWithin(document);
      domReadyHandler = null;
    };
    document.addEventListener('DOMContentLoaded', domReadyHandler, { once: true });
  } else {
    registry.initWithin(document);
  }
})();
